import { useEffect } from 'react';
import { ArrowUpRight, BookUser } from 'lucide-react';

/**
 * `/customer-360` — moved, and this is the part of the move that runs in the browser.
 *
 * Customer 360 became an application of its own on 2026-09-21 (phase P8a of
 * `customer360-app/context/coordination-plans/open/2026-09-18-customer-360-eigenes-repo.md`).
 * The move is a hard cut: all 19 users change at once, there is no parallel operation.
 *
 * ## Why three redirects and not one (D10)
 *
 * Each of the three reaches people the others miss, and this file is the second:
 *
 * 1. A **302** in `staticwebapp.config.json` catches a bookmark or a mail link — a full
 *    page load that actually reaches the server.
 * 2. **This component** catches navigation inside the already-loaded app, which never
 *    reaches the server at all because React Router handles it in the browser.
 * 3. **`web/public/sw-c360-cutover.js`** catches the pages that are already open on this
 *    path behind a warm service worker, which would otherwise answer the navigation out
 *    of its own precache and never let the 302 fire.
 *
 * ## Why the query string is carried along
 *
 * `/customer-duplicates` links here with `?companyCode=…&customerCode=…`. Neither app
 * reads a query string today — measured on 2026-09-21, and the link's own comment said
 * so before that — so carrying it changes nothing right now. It is carried anyway,
 * because dropping it would quietly destroy the information a preselect would need the
 * day the new app grows one.
 *
 * ## The page code is gone
 *
 * P8a left `Customer360Page.tsx` and everything under `components/customer-360/` standing,
 * untouched and unrouted, so that the cutover's rollback was one commit away rather than one
 * restore away. P9 (2026-09-21) deleted it — the move is final now, like `/pure-search` before
 * it. This file and its three companions are what is left: the 302 in
 * `public/staticwebapp.config.json`, the `navigateFallbackDenylist` entry in `vite.config.ts`
 * and `public/sw-c360-cutover.js`. They stay until the bookmarks have died out.
 */
const CUSTOMER_360_APP = 'https://customer360.dolderapps.com';

export function Customer360Moved() {
    const target = CUSTOMER_360_APP + window.location.search + window.location.hash;

    useEffect(() => {
        // replace(), not assign(): the back button should take the user to wherever they
        // came from, not back onto a page that redirects them forward again.
        window.location.replace(target);
    }, [target]);

    // Shown for the moment before the browser follows, and for anyone whose browser
    // refuses to — a note that is useless for a fifth of a second beats a blank screen
    // that is useless for good.
    return (
        <div className="mx-auto max-w-2xl px-4 py-16">
            <div className="mb-4 flex items-center gap-2">
                <BookUser className="h-6 w-6 text-blue-600" />
                <h1 className="text-xl font-semibold text-slate-800">
                    Customer 360 has moved
                </h1>
            </div>

            <p className="text-sm text-slate-600">
                It is an application of its own now, at{' '}
                <span className="font-medium text-slate-800">customer360.dolderapps.com</span>.
                Taking you there…
            </p>

            <a
                href={target}
                className="mt-8 inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2
                           text-sm font-medium text-white transition-colors hover:bg-blue-500"
            >
                Open Customer 360
                <ArrowUpRight className="h-4 w-4" />
            </a>

            <p className="mt-6 text-xs text-slate-400">
                Same customers, same numbers, same tabs — and the visit report is there too.
                If it does not let you in, ask Roman.
            </p>
        </div>
    );
}
